import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { resolveMeetingAppBrand } from "../meetingAdminApp";
import InlineEditableText from "./InlineEditableText";
import {
  WA_EMPTY_SLOT,
  WA_MAX_POINTS,
  WA_MESSAGE_MAX_LENGTH,
  buildSegments,
  resolveMessageParams,
  resolveTokenText,
} from "./meetingReportWaMessage";

const FALLBACK_LOGO = "/resources/images/logo-color-kledo-front.png";

/** Slot kosong ditandai warna berbeda supaya jelas masih bisa diisi. */
const EMPTY_SLOT_CLASS =
  "rounded bg-amber-50 px-1 font-medium not-italic text-amber-600";

const VerifiedBadge = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    style={{ marginLeft: 4 }}
  >
    <path
      d="M7.07143 0L5.78571 1.20202L3.78571 0.777778L3 2.54545L1.28571 2.9697L1.5 4.87879L0 6.22222L1.07143 7.70707L0.5 9.40404L2.14286 10.4646L2.35714 12.303H4.42857L5.71429 14L7.07143 12.8687L8.78572 13.8586L9.78572 12.303H11.7143L12 10.4646L13.6429 9.40404L13 7.70707L14 6.22222L12.6429 4.87879L12.7857 2.9697L11.2143 2.54545L10.2857 0.777778L8.42857 1.20202L7.07143 0Z"
      fill="#256ED3"
    />
    <path
      d="M4.643 6.85706L3.92871 7.7142L6.35728 9.92849L10.4287 4.99992L9.50014 4.2142L6.143 8.2142L4.643 6.85706Z"
      fill="white"
    />
  </svg>
);

/**
 * Pratinjau pesan WhatsApp ringkasan meeting. Body dirakit dari `template` yang
 * dikembalikan endpoint draft (bukan hardcode), 11 variabel:
 * {{1}} sapaan+nama, {{2}} perusahaan, {{3}}..{{10}} poin diskusi (editable),
 * {{11}} nama contact. Jumlah slot poin selalu 8 — tidak bisa ditambah/dikurangi;
 * slot yang dikosongkan tampil "-" persis seperti padding backend.
 * Sapaan dihitung ulang backend saat kirim — di sini hanya preview.
 */
const MeetingReportWhatsappPreview = ({
  appId,
  template,
  greeting,
  contactName,
  company,
  points,
  onChangePoints,
  messageLength = null,
}) => {
  const { t } = useTranslation();
  const brand = resolveMeetingAppBrand(appId);
  const params = resolveMessageParams({ greeting, contactName, company });
  const tooLong = messageLength != null && messageLength > WA_MESSAGE_MAX_LENGTH;

  // Slot bersifat tetap: mengosongkan poin tidak menghapus slotnya, hanya
  // membuatnya tampil "-" (posisi poin lain tidak bergeser).
  const setPointAt = (index, value) => {
    const next = [...points];
    next[index] = value.trim();
    onChangePoints(next);
  };

  // Tanpa maxLength: panjang per poin tidak dibatasi server, yang dibatasi
  // panjang total pesan (lihat penghitung di bawah bubble).
  const renderSlot = (index) => (
    <InlineEditableText
      value={points[index] || ""}
      onChange={(v) => setPointAt(index, v)}
      placeholder={t("meeting_report_wa.edit_point_hint")}
      emptyText={WA_EMPTY_SLOT}
      emptyClassName={EMPTY_SLOT_CLASS}
      ariaLabel={t("meeting_report_wa.point_aria", { number: index + 1 })}
    />
  );

  const renderToken = (slot) =>
    slot >= 3 && slot <= 10
      ? renderSlot(slot - 3)
      : resolveTokenText(slot, params, points);

  let bubbleBody;
  if (template) {
    bubbleBody = buildSegments(template).map((seg, i) => (
      <Fragment key={i}>
        {seg.type === "token" ? renderToken(seg.slot) : seg.value}
      </Fragment>
    ));
  } else {
    // Template pengirim belum dikonfigurasi (not_configured) — tetap tampilkan
    // editor poin agar bisa disiapkan; kirim ditolak 422 sampai template ada.
    bubbleBody = (
      <div>
        <p className="mb-0.5 text-xs font-medium text-neutral-500">
          {t("meeting_report_wa.points_label")}
        </p>
        {Array.from({ length: WA_MAX_POINTS }, (_, i) => (
          <div key={i}>- {renderSlot(i)}</div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <p className="mb-0 text-sm font-semibold text-neutral-800">
        {t("meeting_report_wa.title")}
      </p>
      <p className="mb-3 text-xs text-neutral-500">
        {t("meeting_report_wa.desc")}
      </p>

      <div className="overflow-hidden rounded-2xl border border-neutral-200 shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-neutral-200 bg-[#f0f2f5] px-3 py-2">
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-neutral-200">
            <img
              src={brand.logo}
              alt={brand.name}
              width={22}
              onError={(e) => {
                if (e.currentTarget.src.endsWith(FALLBACK_LOGO)) return;
                e.currentTarget.src = FALLBACK_LOGO;
              }}
            />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="flex items-center text-sm font-semibold text-neutral-900">
              {brand.name}
              <VerifiedBadge />
            </span>
            <span className="text-[11px] text-neutral-500">
              Official Business Account
            </span>
          </div>
        </div>

        {/* Body / chat */}
        <div className="bg-[#efeae2] p-3">
          <div className="whitespace-pre-line rounded-lg rounded-tl-none bg-white p-3 text-sm leading-relaxed text-neutral-800 shadow-sm">
            {bubbleBody}
          </div>
        </div>
      </div>

      {/* Panjang total pesan — batas WhatsApp, bukan batas per poin. */}
      {messageLength != null && (
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <span
            className={
              tooLong
                ? "text-xs font-semibold text-red-500"
                : "text-xs text-neutral-400"
            }
          >
            {t("meeting_report_wa.length_counter", {
              length: messageLength,
              max: WA_MESSAGE_MAX_LENGTH,
            })}
          </span>
          {tooLong && (
            <span className="text-xs text-red-500">
              {t("meeting_report_wa.too_long")}
            </span>
          )}
        </div>
      )}

      <p className="mb-0 mt-2 text-xs text-neutral-400">
        {t("meeting_report_wa.points_limit", { max: WA_MAX_POINTS })}{" "}
        {t("meeting_report_wa.greeting_disclaimer")}
      </p>
    </div>
  );
};

export default MeetingReportWhatsappPreview;
